/**
 * 5 list pages для Підпорції 1.1 (2026-05-25).
 *
 * MVP: list-only для всіх таблиць. Editor pages — окрема міні-порція
 * (TECHDEBT). Sidebar registration у App.tsx.
 */
import { useUnpostedNotes } from '@/shared/hooks/useUnpostedNotes';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { departmentsApi } from '@/api/hr';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { useOrgNames } from '@/shared/hooks/useOrgNames';
import { useAttachmentFlags } from '@/shared/hooks/useAttachmentFlags';
import { useSignControlMulti } from '@/shared/hooks/useSignControl';
import {
  assetGroups,
  depreciationMethods,
  fixedAssets,
  intangibleAssets,
  lowValueAssets,
  assetCommissioning,
  assetWriteOff,
  type AssetGroup,
  type AssetKind,
  type DepreciationMethod,
  type FixedAsset,
  type IntangibleAsset,
  type LowValueAsset,
  type AssetCommissioning,
  type AssetCommissioningItem,
  type AssetStatus,
  type AssetWriteOff,
  type TargetStatus,
  type WriteOffReason,
} from '@/api/assets';


const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const KIND_UA: Record<AssetKind, string> = {
  FIXED_ASSET: 'ОЗ',
  INTANGIBLE: 'НМА',
  LOW_VALUE: 'МНМА',
};


// ============================== Asset Groups ==========================


export function AssetGroupsPage() {
  const [kind, setKind] = useState<AssetKind | ''>('');
  const list = useQuery({
    queryKey: ['asset-groups', kind],
    queryFn: () => assetGroups.list({ kind: kind || undefined, limit: 500 }),
  });
  const cols = useMemo<ColumnDef<AssetGroup>[]>(
    () => [
      { key: 'code', header: 'Код', width: 140, sortValue: (r) => r.code ?? '', cell: (r) => r.code },
      {
        key: 'asset_kind', header: 'Тип', width: 100,
        sortValue: (r) => r.asset_kind ?? '',
        cell: (r) => KIND_UA[r.asset_kind] ?? String(r.asset_kind),
      },
      { key: 'group_number', header: '№', width: 60, align: 'right', sortValue: (r) => r.group_number ?? 0, cell: (r) => r.group_number },
      { key: 'name_uk', header: 'Назва', sortValue: (r) => r.name_uk ?? '', cell: (r) => r.name_uk },
      {
        key: 'min_useful_life_months', header: 'Мін. термін (міс.)',
        width: 160, align: 'right',
        sortValue: (r) => r.min_useful_life_months ?? 0,
        cell: (r) => r.min_useful_life_months,
      },
      { key: 'accounting_account_code', header: 'Рахунок', width: 110, sortValue: (r) => r.accounting_account_code ?? '', cell: (r) => r.accounting_account_code },
      { key: 'depreciation_account_code', header: 'Знос', width: 100, sortValue: (r) => r.depreciation_account_code ?? '', cell: (r) => r.depreciation_account_code },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar" style={{ gap: 12 }}>
        <h2 style={{ margin: 0 }}>Групи активів (ПКУ ст.138.3.3+138.3.4)</h2>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as AssetKind | '')}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          <option value="">Усі</option>
          <option value="FIXED_ASSET">ОЗ (16)</option>
          <option value="INTANGIBLE">НМА (6)</option>
        </select>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<AssetGroup>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає даних."
        initialSortKey="code"
      />
    </>
  );
}


// ============================== Depreciation Methods ==================


export function DepreciationMethodsPage() {
  const list = useQuery({
    queryKey: ['dep-methods'],
    queryFn: () => depreciationMethods.list({ limit: 100 }),
  });
  const cols = useMemo<ColumnDef<DepreciationMethod>[]>(
    () => [
      { key: 'code', header: 'Код', width: 200, sortValue: (r) => r.code ?? '', cell: (r) => r.code },
      { key: 'name_uk', header: 'Назва', width: 280, sortValue: (r) => r.name_uk ?? '', cell: (r) => r.name_uk },
      { key: 'description', header: 'Опис', sortValue: (r) => r.description ?? '', cell: (r) => r.description },
      {
        key: 'is_active', header: 'Активний', width: 120,
        align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Методи амортизації (НП(С)БО 7)</h2>
      </div>
      <DataTable<DepreciationMethod>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає даних."
        initialSortKey="code"
      />
    </>
  );
}


// ============================== Fixed Assets ==========================



/**
 * Редактор картки активу (2026-08-02, рішення власника «зробити як в 1С»):
 * створення + редагування ОЗ/НМА (спільні поля) і МШП (свій набір).
 */
function AssetCardModal({ kind, asset, onClose }: {
  kind: AssetKind;
  asset: (FixedAsset & Partial<LowValueAsset>) | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isLv = kind === 'LOW_VALUE';
  const apiOf = kind === 'FIXED_ASSET' ? fixedAssets
    : kind === 'INTANGIBLE' ? intangibleAssets : (lowValueAssets as never);
  const { availableOrgs } = usePermissionsByOrg('refs.fixed_assets.manage');

  const [f, setF] = useState(() => ({
    organization_id: asset?.organization_id ?? '',
    inventory_number: asset?.inventory_number ?? '',
    name: asset?.name ?? '',
    asset_group_id: asset?.asset_group_id ?? '',
    depreciation_method_id: asset?.depreciation_method_id ?? '',
    primary_cost: asset?.primary_cost ?? '0',
    liquidation_value: asset?.liquidation_value ?? '0',
    useful_life_months: String(asset?.useful_life_months ?? 60),
    expense_account_code: (asset?.expense_account_code ?? '92') as string,
    department_id: asset?.department_id ?? '',
    responsible_employee_id: (asset as { responsible_employee_id?: string | null } | null)
      ?.responsible_employee_id ?? '',
    comment: (asset as { comment?: string | null } | null)?.comment ?? '',
    write_off_method: ((asset as Partial<LowValueAsset> | null)?.write_off_method
      ?? 'SPLIT_50_50') as string,
    quantity: String((asset as Partial<LowValueAsset> | null)?.quantity ?? 1),
  }));
  const [error, setError] = useState<string | null>(null);

  const groupsQ = useQuery({
    queryKey: ['asset-groups', kind],
    queryFn: () => assetGroups.list({ kind, limit: 200 }),
    enabled: !isLv,
  });
  const methodsQ = useQuery({
    queryKey: ['depreciation-methods'],
    queryFn: () => depreciationMethods.list({ limit: 100 }),
    enabled: !isLv,
  });
  const orgIdEff = f.organization_id || availableOrgs[0]?.id || '';
  const departmentsQ = useQuery({
    queryKey: ['departments', orgIdEff],
    queryFn: () => departmentsApi.list({ organization_id: orgIdEff, limit: 200 })
      .then((r) => r.items),
    enabled: !!orgIdEff,
  });

  const save = useMutation({
    mutationFn: () => {
      const base = {
        organization_id: orgIdEff,
        inventory_number: f.inventory_number || null,
        name: f.name.trim(),
        primary_cost: f.primary_cost,
        expense_account_code: f.expense_account_code as never,
        department_id: f.department_id || null,
        responsible_employee_id: f.responsible_employee_id || null,
        comment: f.comment || null,
      };
      const payload = isLv
        ? { ...base,
            write_off_method: f.write_off_method as never,
            quantity: Number(f.quantity) || 1 }
        : { ...base,
            asset_group_id: f.asset_group_id,
            depreciation_method_id: f.depreciation_method_id,
            liquidation_value: f.liquidation_value,
            useful_life_months: Number(f.useful_life_months) || 60 };
      return asset
        ? (apiOf as typeof fixedAssets).update(asset.id, payload as never)
        : (apiOf as typeof fixedAssets).create(payload as never);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] });
      qc.invalidateQueries({ queryKey: ['intangible-assets-v2'] });
      qc.invalidateQueries({ queryKey: ['low-value-assets'] });
      onClose();
    },
    onError: (e: unknown) => {
      const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof d === 'string' ? d : JSON.stringify(d ?? String(e)));
    },
  });

  const canSave = !!f.name.trim() && !!orgIdEff
    && (isLv || (!!f.asset_group_id && !!f.depreciation_method_id));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <div className="card" style={{ width: 640, maxHeight: '85vh', overflowY: 'auto', margin: 0 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>
            {asset ? `Картка: ${asset.name}` : `Нова картка ${KIND_UA[kind]}`}
          </h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Організація</label>
            <select value={orgIdEff} disabled={!!asset}
              onChange={(e) => setF({ ...f, organization_id: e.target.value })}>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Інвентарний № <small style={{ color: 'var(--muted)' }}>(порожньо — авто)</small></label>
            <input value={f.inventory_number}
              onChange={(e) => setF({ ...f, inventory_number: e.target.value })} />
          </div>
        </div>
        <div className="field">
          <label>Назва <span style={{ color: 'red' }}>*</span></label>
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </div>
        {!isLv && (
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Група <span style={{ color: 'red' }}>*</span></label>
              <select value={f.asset_group_id}
                onChange={(e) => setF({ ...f, asset_group_id: e.target.value })}>
                <option value="">— оберіть —</option>
                {(groupsQ.data?.items ?? []).map((g) => (
                  <option key={g.id} value={g.id}>{g.name_uk}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Метод амортизації <span style={{ color: 'red' }}>*</span></label>
              <select value={f.depreciation_method_id}
                onChange={(e) => setF({ ...f, depreciation_method_id: e.target.value })}>
                <option value="">— оберіть —</option>
                {(methodsQ.data?.items ?? []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name_uk}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Первісна вартість</label>
            <input type="number" step="0.01" value={f.primary_cost}
              style={{ textAlign: 'right' }}
              onChange={(e) => setF({ ...f, primary_cost: e.target.value })} />
          </div>
          {!isLv ? (
            <>
              <div className="field" style={{ gridColumn: 'span 4' }}>
                <label>Ліквідаційна вартість</label>
                <input type="number" step="0.01" value={f.liquidation_value}
                  style={{ textAlign: 'right' }}
                  onChange={(e) => setF({ ...f, liquidation_value: e.target.value })} />
              </div>
              <div className="field" style={{ gridColumn: 'span 4' }}>
                <label>Строк корисного викор., міс</label>
                <input type="number" min="1" value={f.useful_life_months}
                  onChange={(e) => setF({ ...f, useful_life_months: e.target.value })} />
              </div>
            </>
          ) : (
            <>
              <div className="field" style={{ gridColumn: 'span 4' }}>
                <label>Метод списання</label>
                <select value={f.write_off_method}
                  onChange={(e) => setF({ ...f, write_off_method: e.target.value })}>
                  <option value="SPLIT_50_50">50/50</option>
                  <option value="AT_USE_100">100% при введенні</option>
                </select>
              </div>
              <div className="field" style={{ gridColumn: 'span 4' }}>
                <label>Кількість</label>
                <input type="number" min="1" value={f.quantity}
                  onChange={(e) => setF({ ...f, quantity: e.target.value })} />
              </div>
            </>
          )}
        </div>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Рахунок витрат {isLv ? 'списання' : 'амортизації'}</label>
            <select value={f.expense_account_code}
              onChange={(e) => setF({ ...f, expense_account_code: e.target.value })}>
              {!isLv && <option value="23">23 — Виробництво</option>}
              <option value="91">91 — Загальновиробничі</option>
              <option value="92">92 — Адміністративні</option>
              <option value="93">93 — Збут</option>
              <option value="94">94 — Інші операційні</option>
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Підрозділ</label>
            <select value={f.department_id ?? ''}
              onChange={(e) => setF({ ...f, department_id: e.target.value })}>
              <option value="">—</option>
              {(departmentsQ.data ?? []).map((d: { id: string; name: string }) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label>Коментар</label>
          <input value={f.comment}
            onChange={(e) => setF({ ...f, comment: e.target.value })} />
        </div>
        {asset && (
          <p style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
            Статус: {asset.status}. Накопичена амортизація і залишкова
            вартість змінюються документами (введення в експлуатацію,
            амортизація, модернізація) — не карткою.
          </p>
        )}
        {error && <div style={{ color: '#dc2626', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="primary" disabled={!canSave || save.isPending}
            onClick={() => save.mutate()}>
            {save.isPending ? 'зберігається…' : 'зберегти'}
          </button>
          <button onClick={onClose}>скасувати</button>
        </div>
      </div>
    </div>
  );
}


export function FixedAssetsPage() {
  const list = useQuery({
    queryKey: ['fixed-assets-v2'],
    queryFn: () => fixedAssets.list({ limit: 500 }),
  });
  const [modal, setModal] = useState<{ asset: FixedAsset | null } | null>(null);
  const cols = useMemo<ColumnDef<FixedAsset>[]>(
    () => [
      { key: 'inventory_number', header: 'Інв. №', width: 160, sortValue: (r) => r.inventory_number ?? '', cell: (r) => r.inventory_number },
      { key: 'name', header: 'Назва', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
      {
        key: 'primary_cost', header: 'Первісна', width: 140,
        align: 'right',
        sortValue: (r) => Number(r.primary_cost),
        cell: (r) => fmt2(r.primary_cost),
      },
      {
        key: 'accumulated_depreciation', header: 'Накоп. амор.', width: 140,
        align: 'right',
        sortValue: (r) => Number(r.accumulated_depreciation),
        cell: (r) => fmt2(r.accumulated_depreciation),
      },
      {
        key: 'residual_value', header: 'Залишкова', width: 140,
        align: 'right',
        sortValue: (r) => Number(r.residual_value),
        cell: (r) => fmt2(r.residual_value),
      },
      { key: 'status', header: 'Статус', width: 130, sortValue: (r) => r.status ?? '', cell: (r) => r.status },
      { key: 'commissioning_date', header: 'Дата введення', width: 140, sortValue: (r) => r.commissioning_date ?? '', cell: (r) => r.commissioning_date },
    ],
    [],
  );
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Основні засоби (ОЗ)</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => setModal({ asset: null })}>
          + Нова картка
        </button>
      </div>
      <DataTable<FixedAsset>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає даних. Створіть картку або додайте обладнання через прихід."
        onRowDoubleClick={(r) => setModal({ asset: r })}
        initialSortKey="inventory_number"
      />
      {modal && (
        <AssetCardModal kind="FIXED_ASSET" asset={modal.asset}
          onClose={() => setModal(null)} />
      )}
    </div>
  );
}


// ============================== Intangible Assets =====================


export function IntangibleAssetsPage() {
  const list = useQuery({
    queryKey: ['intangible-assets-v2'],
    queryFn: () => intangibleAssets.list({ limit: 500 }),
  });
  const [modal, setModal] = useState<{ asset: IntangibleAsset | null } | null>(null);
  const cols = useMemo<ColumnDef<IntangibleAsset>[]>(
    () => [
      { key: 'inventory_number', header: 'Інв. №', width: 160, sortValue: (r) => r.inventory_number ?? '', cell: (r) => r.inventory_number },
      { key: 'name', header: 'Назва', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
      {
        key: 'primary_cost', header: 'Первісна', width: 140,
        align: 'right',
        sortValue: (r) => Number(r.primary_cost),
        cell: (r) => fmt2(r.primary_cost),
      },
      {
        key: 'residual_value', header: 'Залишкова', width: 140,
        align: 'right',
        sortValue: (r) => Number(r.residual_value),
        cell: (r) => fmt2(r.residual_value),
      },
      { key: 'status', header: 'Статус', width: 130, sortValue: (r) => r.status ?? '', cell: (r) => r.status },
    ],
    [],
  );
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нематеріальні активи (НМА)</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => setModal({ asset: null })}>
          + Нова картка
        </button>
      </div>
      <DataTable<IntangibleAsset>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає даних."
        onRowDoubleClick={(r) => setModal({ asset: r })}
        initialSortKey="inventory_number"
      />
      {modal && (
        <AssetCardModal kind="INTANGIBLE" asset={modal.asset}
          onClose={() => setModal(null)} />
      )}
    </div>
  );
}


// ============================== Low Value Assets ======================


export function LowValueAssetsPage() {
  const list = useQuery({
    queryKey: ['low-value-assets'],
    queryFn: () => lowValueAssets.list({ limit: 500 }),
  });
  const [modal, setModal] = useState<{ asset: LowValueAsset | null } | null>(null);
  const cols = useMemo<ColumnDef<LowValueAsset>[]>(
    () => [
      { key: 'inventory_number', header: 'Інв. №', width: 160, sortValue: (r) => r.inventory_number ?? '', cell: (r) => r.inventory_number },
      { key: 'name', header: 'Назва', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
      {
        key: 'primary_cost', header: 'Вартість', width: 130,
        align: 'right',
        sortValue: (r) => Number(r.primary_cost),
        cell: (r) => fmt2(r.primary_cost),
      },
      { key: 'quantity', header: 'Кільк.', width: 90, align: 'right', sortValue: (r) => r.quantity ?? 0, cell: (r) => r.quantity },
      { key: 'write_off_method', header: 'Метод списання', width: 160, sortValue: (r) => r.write_off_method ?? '', cell: (r) => r.write_off_method },
      { key: 'track_mode', header: 'Облік', width: 120, sortValue: (r) => r.track_mode ?? '', cell: (r) => r.track_mode },
      { key: 'status', header: 'Статус', width: 130, sortValue: (r) => r.status ?? '', cell: (r) => r.status },
    ],
    [],
  );
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Малоцінка (МНМА &lt; 20K)</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => setModal({ asset: null })}>
          + Нова картка
        </button>
      </div>
      <DataTable<LowValueAsset>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає даних."
        onRowDoubleClick={(r) => setModal({ asset: r })}
        initialSortKey="inventory_number"
      />
      {modal && (
        <AssetCardModal kind="LOW_VALUE" asset={modal.asset as never}
          onClose={() => setModal(null)} />
      )}
    </div>
  );
}


// ============================== Asset Commissioning ===================



/**
 * Документ активів (2026-08-02, етап 2): «Введення в експлуатацію» /
 * «Списання» — створення з підбором активів (ОЗ/НМА/МШП разом).
 */
function AssetDocModal({ mode, onClose, initialAssetId, initialOrgId }: {
  mode: 'commissioning' | 'writeoff';
  onClose: () => void;
  /** Prefill з картки активу (2026-08-27): ?asset_id=&organization_id= */
  initialAssetId?: string;
  initialOrgId?: string;
}) {
  const qc = useQueryClient();
  const isCommissioning = mode === 'commissioning';
  const { availableOrgs } = usePermissionsByOrg(
    isCommissioning ? 'documents.asset_commissioning.manage'
      : 'documents.asset_write_off.manage');
  const [orgId, setOrgId] = useState(initialOrgId ?? '');
  const [docDate, setDocDate] = useState(new Date().toISOString().slice(0, 10));
  const [targetStatus, setTargetStatus] = useState<TargetStatus>('OPERATING');
  const [reason, setReason] = useState<WriteOffReason>('OBSOLETE');
  const [reasonDetails, setReasonDetails] = useState('');
  const [comment, setComment] = useState('');
  type DocRow = AssetCommissioningItem & { accumulated_depreciation_snapshot?: string };
  const [rows, setRows] = useState<DocRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const orgEff = orgId || availableOrgs[0]?.id || '';

  // Кандидати: введення — PARKED; списання — OPERATING/PARKED.
  const faQ = useQuery({ queryKey: ['fixed-assets-v2', orgEff],
    queryFn: () => fixedAssets.list({ organization_id: orgEff, limit: 500 }),
    enabled: !!orgEff });
  const iaQ = useQuery({ queryKey: ['intangible-assets-v2', orgEff],
    queryFn: () => intangibleAssets.list({ organization_id: orgEff, limit: 500 }),
    enabled: !!orgEff });
  const lvQ = useQuery({ queryKey: ['low-value-assets', orgEff],
    queryFn: () => lowValueAssets.list({ organization_id: orgEff, limit: 500 }),
    enabled: !!orgEff });
  const wanted: AssetStatus[] = isCommissioning
    ? ['PARKED'] : ['OPERATING', 'PARKED', 'CONSERVATION'];
  const used = new Set(rows.map((r) =>
    r.fixed_asset_id ?? r.intangible_asset_id ?? r.low_value_asset_id));
  const candidates: Array<{ kind: AssetKind; a: FixedAsset | LowValueAsset }> = [
    ...(faQ.data?.items ?? []).map((a) => ({ kind: 'FIXED_ASSET' as AssetKind, a })),
    ...(iaQ.data?.items ?? []).map((a) => ({ kind: 'INTANGIBLE' as AssetKind, a })),
    ...(lvQ.data?.items ?? []).map((a) => ({ kind: 'LOW_VALUE' as AssetKind, a })),
  ].filter(({ a }) => wanted.includes(a.status) && !used.has(a.id));

  // Prefill з картки: щойно кандидат зʼявився — додаємо його рядком (один раз).
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (!initialAssetId || prefilled) return;
    const c = candidates.find(({ a }) => a.id === initialAssetId);
    if (c) { addRow(c.kind, c.a); setPrefilled(true); }
  });

  function addRow(kind: AssetKind, a: FixedAsset | LowValueAsset) {
    setRows((rs) => [...rs, {
      line_number: rs.length + 1,
      asset_kind: kind,
      fixed_asset_id: kind === 'FIXED_ASSET' ? a.id : null,
      intangible_asset_id: kind === 'INTANGIBLE' ? a.id : null,
      low_value_asset_id: kind === 'LOW_VALUE' ? a.id : null,
      primary_cost_snapshot: a.primary_cost,
      useful_life_months_snapshot: kind === 'LOW_VALUE'
        ? null : (a as FixedAsset).useful_life_months,
      // Списання: знос у проводку береться зі снапшота рядка (не з картки).
      ...(isCommissioning ? {} : {
        accumulated_depreciation_snapshot: kind === 'LOW_VALUE'
          ? '0' : (a as FixedAsset).accumulated_depreciation,
      }),
    }]);
  }

  const createdId = useRef<string | null>(null);
  const save = useMutation({
    mutationFn: async (andPost: boolean) => {
      const payload = {
        organization_id: orgEff,
        document_number: null,
        document_date: `${docDate}T12:00:00`,
        comment: comment || null,
        items: rows.map((r, i) => (isCommissioning
          ? {
              line_number: i + 1, asset_kind: r.asset_kind,
              fixed_asset_id: r.fixed_asset_id,
              intangible_asset_id: r.intangible_asset_id,
              low_value_asset_id: r.low_value_asset_id,
              primary_cost_snapshot: r.primary_cost_snapshot,
              useful_life_months_snapshot: r.useful_life_months_snapshot,
            }
          : {
              line_number: i + 1, asset_kind: r.asset_kind,
              fixed_asset_id: r.fixed_asset_id,
              intangible_asset_id: r.intangible_asset_id,
              low_value_asset_id: r.low_value_asset_id,
              primary_cost_snapshot: r.primary_cost_snapshot,
              accumulated_depreciation_snapshot:
                r.accumulated_depreciation_snapshot ?? '0',
            })),
        ...(isCommissioning
          ? { target_status: targetStatus }
          : { reason, reason_details: reasonDetails || null }),
      };
      // ч.1343: якщо проведення відмовило, документ уже створено — повторне «провести»
      // оновлює ЙОГО (з виправленнями у вікні), а не створює другий такий самий.
      const prev = createdId.current;
      const doc = isCommissioning
        ? await (prev ? assetCommissioning.update(prev, payload as never) : assetCommissioning.create(payload as never))
        : await (prev ? assetWriteOff.update(prev, payload as never) : assetWriteOff.create(payload as never));
      createdId.current = doc.id;
      if (andPost) {
        if (isCommissioning) await assetCommissioning.post(doc.id);
        else await assetWriteOff.post(doc.id);
      }
      return doc;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['asset-commissioning'] });
      qc.invalidateQueries({ queryKey: ['asset-write-off'] });
      qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] });
      qc.invalidateQueries({ queryKey: ['intangible-assets-v2'] });
      qc.invalidateQueries({ queryKey: ['low-value-assets'] });
      onClose();
    },
    onError: (e: unknown) => {
      const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof d === 'string' ? d : JSON.stringify(d ?? String(e)));
    },
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <div className="card" style={{ width: 760, maxHeight: '85vh', overflowY: 'auto', margin: 0 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>
            {isCommissioning ? 'Введення в експлуатацію' : 'Списання активів'}
          </h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 5' }}>
            <label>Організація</label>
            <select value={orgEff} onChange={(e) => { setOrgId(e.target.value); setRows([]); }}>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Дата</label>
            <input type="date" value={docDate}
              onChange={(e) => setDocDate(e.target.value)} />
          </div>
          {isCommissioning ? (
            <div className="field" style={{ gridColumn: 'span 5' }}>
              <label>Цільовий статус</label>
              <select value={targetStatus}
                onChange={(e) => setTargetStatus(e.target.value as TargetStatus)}>
                <option value="OPERATING">В експлуатації</option>
                <option value="PARKED">На зберіганні</option>
              </select>
            </div>
          ) : (
            <div className="field" style={{ gridColumn: 'span 5' }}>
              <label>Причина</label>
              <select value={reason}
                onChange={(e) => setReason(e.target.value as WriteOffReason)}>
                <option value="OBSOLETE">Моральний/фізичний знос</option>
                <option value="BROKEN">Поломка (не ремонтується)</option>
                <option value="LOST">Втрата / нестача</option>
                <option value="OTHER">Інше</option>
              </select>
            </div>
          )}
        </div>
        {!isCommissioning && reason === 'OTHER' && (
          <div className="field">
            <label>Уточнення причини *</label>
            <input value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)} />
          </div>
        )}
        <h4 style={{ margin: '0.5rem 0 0.25rem' }}>Активи у документі ({rows.length})</h4>
        <table className="tch">
          <tbody>
            {rows.map((r, i) => {
              const all = [
                ...(faQ.data?.items ?? []), ...(iaQ.data?.items ?? []),
                ...(lvQ.data?.items ?? [])];
              const a = all.find((x) => x.id ===
                (r.fixed_asset_id ?? r.intangible_asset_id ?? r.low_value_asset_id));
              return (
                <tr key={i}>
                  <td style={{ width: 60 }}>{KIND_UA[r.asset_kind]}</td>
                  <td>{a?.inventory_number} {a?.name ?? '—'}</td>
                  <td style={{ textAlign: 'right', width: 130 }}>
                    {fmt2(r.primary_cost_snapshot)}
                  </td>
                  <td style={{ width: 30 }}>
                    <button className="danger"
                      onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <h4 style={{ margin: '0.75rem 0 0.25rem' }}>
          Доступні активи ({isCommissioning
            ? 'не введені в експлуатацію' : 'в експлуатації'})
        </h4>
        <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border-soft)', borderRadius: 6 }}>
          <table className="tch">
            <tbody>
              {candidates.length === 0 && (
                <tr><td style={{ color: 'var(--muted)' }}>
                  Немає активів у відповідному статусі.
                </td></tr>
              )}
              {candidates.map(({ kind, a }) => (
                <tr key={a.id} style={{ cursor: 'pointer' }}
                    onClick={() => addRow(kind, a)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                  <td style={{ width: 60 }}>{KIND_UA[kind]}</td>
                  <td>{a.inventory_number} {a.name}</td>
                  <td style={{ textAlign: 'right', width: 130 }}>{fmt2(a.primary_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="field" style={{ marginTop: '0.5rem' }}>
          <label>Коментар</label>
          <input value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        {error && <div style={{ color: '#dc2626', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="primary"
            disabled={!rows.length || save.isPending
              || (!isCommissioning && reason === 'OTHER' && !reasonDetails.trim())}
            onClick={() => save.mutate(true)}>
            {save.isPending ? 'виконується…' : 'провести'}
          </button>
          <button disabled={!rows.length || save.isPending}
            onClick={() => save.mutate(false)}>
            зберегти чернетку
          </button>
          <button onClick={onClose}>скасувати</button>
        </div>
      </div>
    </div>
  );
}


const TARGET_UA: Record<string, string> = {
  OPERATING: 'в експлуатації', PARKED: 'на зберіганні',
};
const REASON_UA: Record<string, string> = {
  OBSOLETE: 'знос', BROKEN: 'поломка', LOST: 'втрата', OTHER: 'інше',
};

export function AssetCommissioningPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(!!sp.get('asset_id'));
  // 2026-08-31 (рішення власника): фільтри орг/період/статус — канон списків.
  const f = useDocListFilters();
  const orgNames = useOrgNames();
  const list = useQuery({
    queryKey: ['asset-commissioning', ...f.queryKey],
    queryFn: () => assetCommissioning.list({
      organization_id: f.orgId || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      date_from: f.dateFrom, date_to: f.dateTo, limit: 500,
    }),
  });
  // ч.1218: перенесені з 1С акти, чиї проводки приїхали довідкою, — сіра відмітка замість червоної.
  const rowsWithNotes = useUnpostedNotes('doc.asset_commissioning', list.data?.items ?? []);
  const attF = useAttachmentFlags('doc.asset_commissioning', list.data?.items ?? []);
  const scF = useSignControlMulti('doc.asset_commissioning', list.data?.items ?? []);
  const cols = useMemo<ColumnDef<AssetCommissioning>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      { key: 'organization_id', header: 'Організація', width: 190, sortValue: (r) => orgNames.get(r.organization_id) ?? '', cell: (r) => orgNames.get(r.organization_id) ?? '' },
      { key: 'target_status', header: 'Цільовий статус', width: 160, sortValue: (r) => r.target_status ?? '', cell: (r) => TARGET_UA[r.target_status ?? ''] ?? r.target_status },
      {
        key: 'items', header: 'Позицій', width: 110,
        align: 'right',
        sortValue: (r) => r.items?.length ?? 0,
        cell: (r) => r.items?.length ?? 0,
      },
      {
        key: 'posted', header: 'Проведено', width: 120,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
      // 2026-08-31 (рішення власника): «провести» зі списку прибрано —
      // проведення у редакторі документа (Закон дизайну #6).
      ...attF.column,
      ...scF.column,
    ],
    [orgNames, attF.column, scF.column],
  );
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Введення в експлуатацію</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/assets/commissioning/new')}>
          + Новий документ
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Проведені' },
          { value: 'draft', label: 'Чернетки' },
        ]} />
      </DocListFiltersRow>
      <DataTable<AssetCommissioning>
        onRowDoubleClick={(r) => navigate(`/assets/commissioning/${r.id}`)}
        rows={rowsWithNotes}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
      {createOpen && (
        <AssetDocModal mode="commissioning" onClose={() => setCreateOpen(false)}
          initialAssetId={sp.get('asset_id') ?? undefined} initialOrgId={sp.get('organization_id') ?? undefined} />
      )}
    </div>
  );
}


// ============================== Asset Write-off =======================


export function AssetWriteOffPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(!!sp.get('asset_id'));
  const f = useDocListFilters();
  const orgNames = useOrgNames();
  const list = useQuery({
    queryKey: ['asset-write-off', ...f.queryKey],
    queryFn: () => assetWriteOff.list({
      organization_id: f.orgId || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      date_from: f.dateFrom, date_to: f.dateTo, limit: 500,
    }),
  });
  // ч.1218: перенесені з 1С акти, чиї проводки приїхали довідкою, — сіра відмітка замість червоної.
  const rowsWithNotes = useUnpostedNotes('doc.asset_write_off', list.data?.items ?? []);
  const attF = useAttachmentFlags('doc.asset_write_off', list.data?.items ?? []);
  const scF = useSignControlMulti('doc.asset_write_off', list.data?.items ?? []);
  const cols = useMemo<ColumnDef<AssetWriteOff>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      { key: 'organization_id', header: 'Організація', width: 190, sortValue: (r) => orgNames.get(r.organization_id) ?? '', cell: (r) => orgNames.get(r.organization_id) ?? '' },
      { key: 'reason', header: 'Причина', width: 130, sortValue: (r) => r.reason ?? '', cell: (r) => REASON_UA[r.reason ?? ''] ?? r.reason },
      {
        key: 'items', header: 'Позицій', width: 110,
        align: 'right',
        sortValue: (r) => r.items?.length ?? 0,
        cell: (r) => r.items?.length ?? 0,
      },
      {
        key: 'posted', header: 'Проведено', width: 120,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
      // 2026-08-31: «провести» зі списку прибрано (Закон #6) + вкладення/підпис.
      ...attF.column,
      ...scF.column,
    ],
    [orgNames, attF.column, scF.column],
  );
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Списання активів</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/assets/write-off/new')}>
          + Новий документ
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Проведені' },
          { value: 'draft', label: 'Чернетки' },
        ]} />
      </DocListFiltersRow>
      <DataTable<AssetWriteOff>
        onRowDoubleClick={(r) => navigate(`/assets/write-off/${r.id}`)}
        rows={rowsWithNotes}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
      {createOpen && (
        <AssetDocModal mode="writeoff" onClose={() => setCreateOpen(false)}
          initialAssetId={sp.get('asset_id') ?? undefined} initialOrgId={sp.get('organization_id') ?? undefined} />
      )}
    </div>
  );
}
